
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CITIES, RANKS } from '../constants';
import { PlayerProfile, IdolRank } from '../types';
import { MapPin, Globe, Plane, Lock, Crown, Signal, Skull, Shield, Sparkles, Music } from 'lucide-react';
import { ExitButton } from './ExitButton';

interface TourMapProps {
    player: PlayerProfile;
    onTravel: (destination: string) => void;
    onExit: () => void;
}

export const TourMap: React.FC<TourMapProps> = ({ player, onTravel, onExit }) => {
    const currentCityData = CITIES[player.currentCity];

    return (
        <div className="h-full w-full bg-slate-950 flex flex-col items-center justify-center p-8 relative overflow-hidden">
            {/* Decorative Background Map Grid */}
            <div className="absolute inset-0 z-0 opacity-20"
                style={{
                    backgroundImage: 'radial-gradient(#ec4899 1px, transparent 1px)',
                    backgroundSize: '40px 40px',
                    transform: 'perspective(500px) rotateX(20deg)'
                }}>
            </div>

            <ExitButton onExit={onExit} label="CLOSE MAP" />

            <div className="relative z-10 w-full max-w-6xl h-full max-h-[85vh] flex flex-col gap-6">

                <header className="flex justify-between items-end border-b-4 border-slate-800 pb-6 bg-slate-900/80 p-6 rounded-t-xl backdrop-blur-md shrink-0">
                    <div>
                        <h2 className="text-5xl font-heading text-white tracking-tighter drop-shadow-[0_0_10px_rgba(255,255,255,0.5)]">
                            HONMOON DEFENSE TOUR
                        </h2>
                        <div className="flex items-center gap-4 mt-2">
                            <span className="text-pink-500 font-bold uppercase tracking-widest text-sm flex items-center gap-2">
                                <Shield size={14} /> Cities Sealed: {player.conqueredCities.length} / {Object.keys(CITIES).length}
                            </span>
                            <span className="w-1 h-1 bg-slate-600 rounded-full"></span>
                            <span className="text-green-400 font-bold uppercase tracking-widest text-sm flex items-center gap-2">
                                <Signal size={14} /> Barrier Status: {player.conqueredCities.length === Object.keys(CITIES).length ? 'GOLDEN' : 'STABILIZING'}
                            </span>
                        </div>
                    </div>
                    <div className="text-right">
                        <div className="text-xs text-slate-500 uppercase tracking-widest mb-1">Current Location</div>
                        <div className="text-3xl font-heading text-pink-400">{currentCityData.name.toUpperCase()}</div>
                    </div>
                </header>

                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 overflow-y-auto p-4 custom-scrollbar pb-20">
                    {Object.entries(CITIES).map(([key, data]) => {
                        const isLocked = player.rank < data.minRank;
                        const isCurrent = player.currentCity === key;
                        const isConquered = player.conqueredCities.includes(key);
                        const reqRankLabel = (RANKS[data.minRank] || RANKS[IdolRank.TRAINEE]).label;

                        return (
                            <div
                                key={key}
                                className={`relative rounded-xl border-2 transition-all duration-300 overflow-hidden group min-h-[250px] flex flex-col
                                ${isCurrent
                                        ? 'border-pink-500 bg-slate-800/80 shadow-[0_0_30px_rgba(236,72,153,0.3)] cursor-default'
                                        : isLocked
                                            ? 'border-slate-800 bg-slate-900/50 grayscale opacity-60 cursor-not-allowed'
                                            : isConquered
                                                ? 'border-green-500/30 bg-green-900/10'
                                                : 'border-red-500/50 bg-slate-800/40 hover:border-red-500 hover:bg-slate-700'
                                    }
                            `}
                            >
                                {/* Status Badge */}
                                <div className="absolute top-4 right-4 z-20">
                                    {isConquered && <Sparkles className="text-yellow-400 fill-yellow-400 animate-pulse" size={24} />}
                                    {isLocked && <Lock className="text-slate-500" size={24} />}
                                    {isCurrent && <MapPin className="text-pink-500 fill-pink-500" size={24} />}
                                </div>

                                <div className="p-6 h-full flex flex-col">
                                    <div className="text-xs font-bold text-slate-500 tracking-widest mb-2 uppercase">
                                        Threat Level {data.difficultyMultiplier.toFixed(1)}
                                    </div>
                                    <h3 className={`text-3xl font-heading mb-4 ${isCurrent ? 'text-pink-400' : 'text-slate-200'}`}>{data.name}</h3>

                                    <div className="space-y-2 mb-6 flex-1">
                                        <div className="flex justify-between text-sm border-b border-slate-700/50 pb-1">
                                            <span className="text-slate-400">Fan Souls at Risk</span>
                                            <span className="text-white font-bold">
                                                {data.population >= 1000000
                                                    ? `${(data.population / 1000000).toFixed(1)}M`
                                                    : data.population >= 1000
                                                        ? `${(data.population / 1000).toFixed(0)}K`
                                                        : data.population}
                                            </span>
                                        </div>
                                        <div className="flex justify-between text-sm border-b border-slate-700/50 pb-1">
                                            <span className="text-slate-400">Status</span>
                                            <span className={`${isConquered ? 'text-green-400' : 'text-red-400'} font-bold`}>
                                                {isConquered ? 'SEALED' : 'DEMON INCURSION'}
                                            </span>
                                        </div>
                                    </div>

                                    <div className="mt-auto">
                                        {isCurrent ? (
                                            <div className="w-full py-3 bg-pink-500/20 border border-pink-500/50 text-pink-300 text-center rounded font-bold uppercase tracking-widest flex items-center justify-center gap-2">
                                                <MapPin size={16} /> CURRENT LOCATION
                                            </div>
                                        ) : isLocked ? (
                                            <div className="w-full py-3 bg-slate-800 border border-slate-700 text-slate-500 text-center rounded font-bold uppercase tracking-widest flex items-center justify-center gap-2">
                                                <Lock size={14} /> Requires {reqRankLabel}
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => onTravel(key)}
                                                className="w-full py-3 text-white font-heading text-lg rounded shadow-lg transition-all flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 cursor-pointer border-b-4 border-blue-800 active:border-b-0 active:translate-y-1"
                                            >
                                                <Plane size={18} /> TRAVEL ($10k)
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
